import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { toast } from "sonner";

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

/**
 * Toast notification utilities for consistent error and success messages
 */
export const Toast = {
	/**
	 * Show error toast notification
	 */
	error: (message: string, description?: string) => {
		toast.error(message, {
			description,
			action: description
				? undefined
				: {
						label: "Đóng",
						onClick: () => {},
					},
		});
	},

	/**
	 * Show success toast notification
	 */
	success: (message: string, description?: string) => {
		toast.success(message, {
			description,
		});
	},

	/**
	 * Show info toast notification
	 */
	info: (message: string, description?: string) => {
		toast.info(message, {
			description,
		});
	},

	/**
	 * Show warning toast notification
	 */
	warning: (message: string, description?: string) => {
		toast.warning(message, {
			description,
		});
	},

	/**
	 * Show an error toast that never auto-dismisses, with a single action button.
	 *
	 * For states the user must act on rather than notice in passing — the toast
	 * IS the recovery path, so letting it time out would strand them. Pass a
	 * stable `id` so repeated calls replace the toast instead of stacking copies.
	 */
	persistentError: (
		message: string,
		description: string,
		action: { label: string; onClick: () => void },
		id?: string,
	) => {
		toast.error(message, {
			description,
			duration: Infinity,
			id,
			action,
		});
	},

	/**
	 * Dismiss a toast by id (used to clear a persistent one once it is resolved).
	 */
	dismiss: (id: string) => {
		toast.dismiss(id);
	},

	/**
	 * Show loading toast with promise handling
	 */
	promise: <T>(
		promise: Promise<T>,
		messages: {
			loading: string;
			success: string;
			error: string;
		},
	): void => {
		toast.promise(promise, messages);
	},
};

/**
 * Standard error messages for common scenarios
 */
export const ErrorMessages = {
	NETWORK_ERROR: "Không thể kết nối đến máy chủ",
	AUTH_REQUIRED: "Vui lòng đăng nhập lại",
	NOT_FOUND: "Không tìm thấy dữ liệu yêu cầu",
	VALIDATION_ERROR: "Dữ liệu không hợp lệ",
	SERVER_ERROR: "Lỗi máy chủ, vui lòng thử lại sau",
	PERMISSION_DENIED: "Bạn không có quyền thực hiện thao tác này",
	UNKNOWN_ERROR: "Đã có lỗi xảy ra",
};

/**
 * Safe error message extraction utility
 */
export const getSafeErrorMessage = (error: unknown): string => {
	if (error instanceof Error) {
		return error.message;
	}
	if (typeof error === "string") {
		return error;
	}
	if (error && typeof error === "object" && "message" in error) {
		return String((error as any).message);
	}
	return ErrorMessages.UNKNOWN_ERROR;
};

/**
 * Enhanced error handling wrapper for async functions
 */
export const withErrorHandling = async <T>(
	fn: () => Promise<T>,
	context?: string,
): Promise<T | null> => {
	try {
		return await fn();
	} catch (error) {
		const message = getSafeErrorMessage(error);
		console.error(`Error in ${context || "async operation"}:`, error);

		// Show appropriate toast based on error type
		if (message.toLowerCase().includes("network") || message.toLowerCase().includes("fetch")) {
			Toast.error(ErrorMessages.NETWORK_ERROR);
		} else if (message.toLowerCase().includes("auth")) {
			Toast.error(ErrorMessages.AUTH_REQUIRED);
		} else {
			Toast.error(context ? `Lỗi ${context}` : "Đã xảy ra lỗi", message.slice(0, 100));
		}

		return null;
	}
};
